/**
 * Rate Place Modal Component
 * 
 * Modal for rating and reviewing a place
 */

import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Modal, Pressable, ScrollView, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '../ui/Card';
import { ThemedText, Body, Caption, SubHeading } from '../ui/Text';
import { Button } from '../ui/Button';
import { InputField } from '../ui/InputField';
import { ratePlace, getUserPlaceRating, generatePlaceId, type PlaceRating } from '@/services/placeRatings';
import type { LocationCoordinates } from '@/services/location';
import { useUserStore } from '@/state/userStore';

type RatePlaceModalProps = {
  visible: boolean;
  onClose: () => void;
  place: {
    name: string;
    coordinates: LocationCoordinates;
    category?: string;
    address?: string;
  };
  tripId?: string;
  onRated?: () => void;
};

export const RatePlaceModal: React.FC<RatePlaceModalProps> = ({
  visible,
  onClose,
  place,
  tripId,
  onRated,
}) => {
  const theme = useTheme();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [review, setReview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRating, setExistingRating] = useState<PlaceRating | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visible && firebaseUser?.uid) {
      loadExistingRating();
    } else if (visible) {
      setLoading(false);
    }
  }, [visible, firebaseUser?.uid, place.coordinates]);

  const loadExistingRating = async () => {
    if (!firebaseUser?.uid) return;
    
    setLoading(true);
    try {
      const placeId = generatePlaceId(place.coordinates, place.name);
      const existing = await getUserPlaceRating(placeId, firebaseUser.uid);
      if (existing) {
        setExistingRating(existing);
        setRating(existing.rating);
        setReview(existing.review || '');
      }
    } catch (error) {
      console.error('[RatePlaceModal] Error loading existing rating:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingPress = (value: number) => {
    setRating(value);
  };

  const handleSubmit = async () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Authentication Required', 'Please log in to rate places.');
      return;
    }

    if (rating === 0) {
      Alert.alert('Rating Required', 'Please select a rating (1-5 stars).');
      return;
    }

    setIsSubmitting(true);
    try {
      const placeId = generatePlaceId(place.coordinates, place.name);
      await ratePlace(firebaseUser.uid, {
        placeId,
        placeName: place.name,
        coordinates: place.coordinates,
        rating,
        review: review.trim() || undefined,
        tripId,
        category: place.category,
      });

      Alert.alert(
        existingRating ? 'Rating Updated' : 'Thank You!',
        existingRating
          ? 'Your rating has been updated.'
          : 'Your rating has been submitted. Thank you for helping other travelers!',
        [
          {
            text: 'OK',
            onPress: () => {
              onRated?.();
              onClose();
            },
          },
        ]
      );
    } catch (error) {
      console.error('[RatePlaceModal] Error submitting rating:', error);
      Alert.alert(
        'Error',
        error instanceof Error ? error.message : 'Failed to submit rating. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRating(0);
      setReview('');
      setHoveredRating(0);
      setExistingRating(null);
      onClose();
    }
  };

  const displayRating = hoveredRating > 0 ? hoveredRating : rating;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable
        style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}
        onPress={handleClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}
        >
          <Card style={styles.card}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerContent}>
                <SubHeading style={styles.title}>
                  {existingRating ? 'Update Rating' : 'Rate This Place'}
                </SubHeading>
                <Body color={theme.colors.textSecondary} style={styles.placeName}>
                  {place.name}
                </Body>
              </View>
              <Pressable onPress={handleClose} style={styles.closeButton}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            {loading ? (
              <View style={styles.loadingContainer}>
                <Body color={theme.colors.textSecondary}>Loading...</Body>
              </View>
            ) : (
              <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
                {/* Rating Stars */}
                <View style={styles.ratingSection}>
                  <Body style={styles.ratingLabel}>Your Rating</Body>
                  <View style={styles.starsContainer}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Pressable
                        key={value}
                        onPress={() => handleRatingPress(value)}
                        onPressIn={() => setHoveredRating(value)}
                        onPressOut={() => setHoveredRating(0)}
                        style={styles.starButton}
                      >
                        <Ionicons
                          name={value <= displayRating ? 'star' : 'star-outline'}
                          size={40}
                          color={
                            value <= displayRating
                              ? theme.colors.primary
                              : theme.colors.borderMuted
                          }
                        />
                      </Pressable>
                    ))}
                  </View>
                  {displayRating > 0 && (
                    <Caption color={theme.colors.textSecondary} style={styles.ratingText}>
                      {displayRating} out of 5 stars
                    </Caption>
                  )}
                </View>

                {/* Review Text */}
                <View style={styles.reviewSection}>
                  <InputField
                    label="Review (Optional)"
                    placeholder="Share your experience at this place..."
                    value={review}
                    onChangeText={setReview}
                    multiline
                    numberOfLines={5}
                    style={styles.reviewInput}
                  />
                  <Caption color={theme.colors.textSecondary} style={styles.reviewHint}>
                    Help other travelers by sharing your experience
                  </Caption>
                </View>

                {/* Place Info */}
                {place.address && (
                  <View style={styles.placeInfo}>
                    <Caption color={theme.colors.textSecondary}>
                      📍 {place.address}
                    </Caption>
                  </View>
                )}
              </ScrollView>
            )}

            {/* Footer */}
            <View style={[styles.footer, { borderTopColor: theme.colors.borderMuted }]}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={handleClose}
                disabled={isSubmitting}
                style={styles.cancelButton}
              />
              <Button
                label={existingRating ? 'Update Rating' : 'Submit Rating'}
                variant="primary"
                onPress={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || rating === 0}
                style={styles.submitButton}
              />
            </View>
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    borderRadius: 16,
  },
  card: {
    padding: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    paddingBottom: 16,
  },
  headerContent: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
  },
  placeName: {
    fontSize: 14,
  },
  closeButton: {
    padding: 4,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingTop: 0,
    gap: 24,
  },
  ratingSection: {
    alignItems: 'center',
    gap: 12,
  },
  ratingLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  starButton: {
    padding: 4,
  },
  ratingText: {
    fontSize: 13,
  },
  reviewSection: {
    gap: 8,
  },
  reviewInput: {
    marginBottom: 4,
  },
  reviewHint: {
    fontSize: 11,
  },
  placeInfo: {
    paddingTop: 8,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
});

